import { useMemo, useEffect, useCallback } from 'react';

import { useFormContext } from 'react-hook-form';
import Asterisk from '@decisiv/iconix/lib/components/Asterisk';
import { Flex, Message, Loading } from '@decisiv/ui-components/lib/components';
import Grid from '@decisiv/ui-components/lib/components/Grid';
import { TextField, Select, TextArea } from 'components/Form';
import { DropdownListItem } from '@decisiv/ui-components';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import { getStatesByCountry } from 'utils/countryRegions';

import { Trans, t } from '@lingui/macro';

import { COUNTRY_OPTIONS } from 'pages/Management/constants';
import fleetAdminDeniedReasonText from 'utils/User/fleetAdminDeniedReason';
import { FormContainer } from './styles';
import { useManagedPlatformAccountsQuery } from './hooks';

const CLEAR_STATE_SELECT = t`Clear State`;

interface DepotFormProps {
  accountId?: string;
  accountsQuery: ReturnType<typeof useManagedPlatformAccountsQuery>;
  capability: FleetAdminCapability | null;
}

export default function DepotForm({
  accountId,
  accountsQuery,
  capability,
}: DepotFormProps) {
  const { watch, setValue } = useFormContext();
  const countryValue = watch('country');

  const {
    data: accounts,
    loading: loadingAccounts,
    loadingMore: loadingMoreAccounts,
    loadMore: loadMoreAccounts,
    hasNextPage: hasNextPageAccounts,
  } = accountsQuery;

  const accountsOptions: DropdownListItem[] = useMemo(() => {
    if (!accounts?.managedPlatformAccounts?.collection) return [];

    const formattedData = accounts.managedPlatformAccounts.collection.map(
      (account) => ({
        id: account.id,
        label: account.name,
        value: account.id,
        sourceId: account.sourceId,
      }),
    );

    return formattedData;
  }, [accounts]);

  /* istanbul ignore next -- @preserve */
  useEffect(() => {
    if (!hasNextPageAccounts || loadingAccounts || loadingMoreAccounts) return;
    loadMoreAccounts();
  }, [
    hasNextPageAccounts,
    loadingAccounts,
    loadingMoreAccounts,
    loadMoreAccounts,
  ]);

  const stateOptions = useMemo(
    () => (countryValue ? getStatesByCountry(countryValue) : []),
    [countryValue],
  );

  const handleOnCountryChange = useCallback(() => {
    setValue('state', '');
  }, [setValue]);

  const isDenied = capability?.allowed === false;

  /* istanbul ignore next -- @preserve */
  const LoadingOptions = () => (
    <P size="small" color="charcoal" paddingTop={1} paddingBottom={0.2}>
      <Loading size="small" title={t`Loading Accounts...`} />
    </P>
  );

  return (
    <FormContainer>
      <Grid.Row>
        <Grid.Column>
          <Flex justifyContent="flex-end">
            <Message color="information" icon={Asterisk}>
              <Trans>Required Fields</Trans>
            </Message>
          </Flex>
        </Grid.Column>
      </Grid.Row>
      {!accountId && (
        <Grid.Row>
          <Select
            label={t`Account`}
            name="accountId"
            options={accountsOptions}
            renderOptionLabel={loadingAccounts ? LoadingOptions : undefined}
            defaultValue=""
            required
          />
        </Grid.Row>
      )}
      {isDenied && capability && (
        <Grid.Row>
          <Grid.Column>
            <Message intent="warning">
              {fleetAdminDeniedReasonText(capability.deniedReason)}
            </Message>
          </Grid.Column>
        </Grid.Row>
      )}
      <Grid.Row>
        <TextField
          label={t`FMS ID`}
          name="dmsId"
          maxLength={40}
          helpMessage={t`Max 40 Characters (Alphanumeric only)`}
          disabled={isDenied}
        />
      </Grid.Row>
      <Grid.Row>
        <TextArea
          label={t`Notes`}
          name="note"
          maxLength={350}
          helpMessage={t`You have ${
            350 - (watch('note')?.length || 0)
          } characters left (350 max).`}
          disabled={isDenied}
        />
      </Grid.Row>
      <Grid.Row>
        <TextField label={t`Name`} name="name" required disabled={isDenied} />
      </Grid.Row>
      <Grid.Row>
        <TextField label={t`Email`} name="email" required disabled={isDenied} />
      </Grid.Row>
      <Grid.Row>
        <TextField label={t`Address`} name="address" disabled={isDenied} />
      </Grid.Row>
      <Grid.Row>
        <Grid.Column marginLeft={0} paddingLeft={0} paddingRight={0.5}>
          <Select
            label={t`Country`}
            name="country"
            options={COUNTRY_OPTIONS}
            onChange={handleOnCountryChange}
            defaultValue=""
            required
            disabled={isDenied}
          />
        </Grid.Column>
        <Grid.Column marginRight={0} paddingRight={0} paddingLeft={0.5}>
          <Select
            clearButtonLabel={CLEAR_STATE_SELECT}
            label={t`State`}
            name="state"
            options={stateOptions}
            defaultValue=""
            required
            disabled={isDenied}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column marginLeft={0} paddingLeft={0} paddingRight={0.5}>
          <TextField label={t`City`} name="city" required disabled={isDenied} />
        </Grid.Column>
        <Grid.Column marginRight={0} paddingRight={0} paddingLeft={0.5}>
          <TextField
            label={t`Postal Code`}
            name="postalCode"
            disabled={isDenied}
          />
        </Grid.Column>
      </Grid.Row>
    </FormContainer>
  );
}
